import React, { type HTMLAttributes, useEffect } from 'react';
import { css } from 'styled-components';
import { differenceInHours, parse } from 'date-fns';
import { useSearchParams } from 'next/navigation';

import { IconCircleInfoSolid } from '@/app/common/icons/libraryIcons';
import { InfoContainer } from '@/app/common/designSystemTypedComponents.ts';
import { useToggle } from 'react-use';
import { useTranslation } from '@/app/components/translations/Translation';
import { useOffersContext } from '@/app/components/hotel-detail/OffersProvider';
import { URLParameters } from '@/app/common/urlParameters';
import { useUrlSearchParams } from '../UrlSearchParamsProvider';

const MAX_HOURS = 10;

const IconInfo = () => (
  <IconCircleInfoSolid
    css={css`
      font-size: ${({ theme }) => theme.kallisto.font.size['300']};
    `}
  />
);

export type TimeCheckoutDepartureMessageProps = {
  isTrain: boolean;
  checkoutTo?: string;
  departureDate?: string;
} & HTMLAttributes<HTMLElement>;

/*
 * This message appears when the time from the hotel check out to the flight/train departure is more than ${MAX_HOURS} hours.
 */
export default function TimeCheckoutDepartureMessage(props: TimeCheckoutDepartureMessageProps) {
  const { t } = useTranslation();
  const { isTrain, checkoutTo, departureDate } = props;
  const searchParams = useSearchParams();

  const { offersData } = useOffersContext();
  const { checkout: urlCheckout } = useUrlSearchParams();

  const checkout = offersData?.checkout || urlCheckout;

  const [isVisible, hide] = useToggle(true);
  const [showMessage, setShowMessage] = React.useState(false);

  // Check if custom accommodation is active
  const hasCustomAccommodation = !!searchParams.get(URLParameters.CUSTOM_CHECKIN_CHECKOUT);

  useEffect(() => {
    if (checkout && departureDate) {
      const checkoutDate = parse(`${checkout}#${checkoutTo}`, 'yyyyMMdd#kk:mm', new Date());
      const parsedDepartureDate = parse(departureDate, 'dd/MM/yy#kk:mm', new Date());

      const hoursDifference = differenceInHours(parsedDepartureDate, checkoutDate, { roundingMethod: 'round' });

      if (hoursDifference > MAX_HOURS) {
        // For custom accommodation, only show warning if checkout and departure are on the same day
        if (hasCustomAccommodation) {
          const checkoutDateOnly = parse(checkout, 'yyyyMMdd', new Date());
          const departureDateOnly = parse(departureDate.split('#')[0], 'dd/MM/yy', new Date());

          setShowMessage(checkoutDateOnly.getTime() === departureDateOnly.getTime());
        } else {
          setShowMessage(true);
        }
      } else {
        setShowMessage(false);
      }
    }
  }, [checkout, checkoutTo, departureDate, hasCustomAccommodation]);

  return showMessage && isVisible ? (
    <InfoContainer
      icon={IconInfo}
      message={
        isTrain
          ? t('vacation.select.messages.timecheckoutdeparturetrain')
          : t('vacation.select.messages.timecheckoutflightdeparture')
      }
      onClose={hide}
      type="specific"
      hasBorder={true}
    />
  ) : null;
}
